import React, { Component } from "react";
import * as Cookies from "es-cookie";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as compose from "lodash.flowright";
import { withRouter, Link } from "react-router-dom";
import moment from "moment";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { pointTooltip } from "./chartTooltip";
import ChartBoundary from "./ChartBoundary";
import { PageShimmer } from "./Shimmer";
import { examStreamsVar, examKeysVar, examStreamsKey } from "./examStreams";

// Self-contained modern Complexity tab, driven by getComplexityExamDetails (one row per
// exam of the header's selected exam, last year): KPI tiles + accuracy-by-difficulty chart
// (summed over those exams) → subject-wise / question-type / exam-wise complexity. Same as
// the college ComplexityAnalysisTab; the student app keys on the logged-in enrollment (student_user_exam_id).

const FETCH_ACCESS = gql`
  query ($student_user_exam_id: Int!) {
    isStudentUserValid(student_user_exam_id: $student_user_exam_id) {
      estatus
      user_access_restictions
    }
  }
`;

// Per-exam complexity breakdown — every exam in scope (the header's selected exam + a
// one-year window, like the other profile tabs), with correct / wrong / skipped / accuracy
// for each complexity level.
const FETCH_COMPLEXITY_EXAMS = gql`
  query (
    $student_user_exam_id: Int!
    $exam_type: Int
    $exam_streams: [Int]
    $exam_keys: [String]
    $fromDate: String
    $toDate: String
  ) {
    getStudentPerformanceReport(
      student_user_exam_id: $student_user_exam_id
      exam_type: $exam_type
      exam_streams: $exam_streams
      exam_keys: $exam_keys
      fromDate: $fromDate
      toDate: $toDate
    ) {
      getComplexityExamDetails {
        exam_paper_id
        exam_name
        timestamp
        total_questions
        levels {
          complexity_id
          complexity
          correct
          wrong
          skipped
          attempted
          total
          accuracy
        }
        subjects {
          subject_id
          subject
          total_questions
          levels {
            complexity_id
            complexity
            correct
            wrong
            skipped
            attempted
            total
            accuracy
          }
        }
        question_types {
          question_type_id
          question_type
          difficulty_id
          difficulty
          total_questions
          attempted
          correct
          accuracy_percentage
        }
      }
    }
  }
`;

// Fixed difficulty columns of the Question Type × Difficulty table (matched by name).
const QT_LEVELS = ["Easy", "Moderate", "Difficult", "Highly Difficult"];

// Display config for the known levels (matched by name); any other level gets the fallback.
const LEVEL_STYLE = {
  easy: { color: "#16a34a", icon: "fa-feather", iconBg: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)" },
  moderate: { color: "#ca8a04", icon: "fa-wave-square", iconBg: "linear-gradient(135deg, #facc15 0%, #ca8a04 100%)" },
  difficult: { color: "#ea580c", icon: "fa-fire", iconBg: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)" },
  "highly difficult": { color: "#dc2626", icon: "fa-skull", iconBg: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)" },
};
const levelStyle = (name) =>
  LEVEL_STYLE[`${name || ""}`.toLowerCase()] || { color: "#64748b", icon: "fa-layer-group", iconBg: "linear-gradient(135deg, #94a3b8 0%, #64748b 100%)" };

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

// Accuracy → heat color (weak = red, strong = green).
const heatColor = (pct) => {
  const p = num(pct);
  if (p <= 0) return "#f1f5f9";
  if (p < 40) return "#fee2e2";
  if (p < 60) return "#ffedd5";
  if (p < 75) return "#fef9c3";
  if (p < 90) return "#dcfce7";
  return "#bbf7d0";
};
const heatText = (pct) => (num(pct) <= 0 ? "#94a3b8" : "#1f2937");

class ComplexityTab extends Component {
  state = {
    locked: false, // subscription access restriction (student app)
    examDetails: [], // getComplexityExamDetails — one row per exam
    examDetailsLoading: true,
    subjectView: "table", // "table" | "graph" — Subject-wise Complexity view
    examView: "table", // "table" | "graph" — Exam-wise Complexity view
    qtExam: "all", // Question Type × Difficulty: "all" or an exam_paper_id
    qtView: "table", // "table" | "graph" — Question Type × Difficulty view
  };

  componentDidMount() {
    this.loadExamDetails();
  }

  componentDidUpdate(prevProps) {
    // Follows the header Exam selection (exam_streams / exam_keys).
    if (examStreamsKey(prevProps.examStreams) !== examStreamsKey(this.props.examStreams) ||
      examStreamsKey(prevProps.examKeys) !== examStreamsKey(this.props.examKeys)) {
      this.loadExamDetails();
    }
  }

  // Exam-wise complexity: the same report arguments the other tabs send (selected exam,
  // one-year window ending today). Only the latest request may write state.
  loadExamDetails = async () => {
    this.examSeq = (this.examSeq || 0) + 1;
    const seq = this.examSeq;
    const student_user_exam_id = parseInt(Cookies.get("student_user_exam_id"));
    this.setState({ examDetailsLoading: true, qtExam: "all" }); // a new exam list → back to All exams
    try {
      // Access check (the student app's subscription lock, kept from the old tab).
      const access = await this.props.client.query({
        query: FETCH_ACCESS, variables: { student_user_exam_id }, fetchPolicy: "cache-first",
      });
      if (seq !== this.examSeq) return;
      const valid = access?.data?.isStudentUserValid;
      if (valid) localStorage.setItem("isStudentUserValid", JSON.stringify(valid));
      let restrictions = {};
      try { restrictions = valid && valid.user_access_restictions ? JSON.parse(valid.user_access_restictions) : {}; } catch (e) { restrictions = {}; }
      const isInstitute119 = parseInt(Cookies.get("institution_id"), 10) === 119;
      const locked = isInstitute119
        ? (restrictions.analysis_tab !== true || restrictions.ra_complexity_details !== true)
        : (restrictions.analysis_tab === true || restrictions.ra_complexity_details === true);
      if (locked) {
        this.setState({ locked: true, examDetails: [], examDetailsLoading: false });
        return;
      }

      const res = await this.props.client.query({
        query: FETCH_COMPLEXITY_EXAMS,
        variables: {
          student_user_exam_id,
          exam_type: 0,
          exam_streams: examStreamsVar(this.props.examStreams),
          exam_keys: examKeysVar(this.props.examKeys),
          fromDate: String(moment().subtract(1, "year").startOf("day").unix()),
          toDate: String(moment().endOf("day").unix()),
        },
        fetchPolicy: "cache-first",
      });
      if (seq !== this.examSeq) return;
      this.setState({
        locked: false,
        examDetails: res?.data?.getStudentPerformanceReport?.getComplexityExamDetails || [],
        examDetailsLoading: false,
      });
    } catch (e) {
      console.log(e);
      if (seq !== this.examSeq) return;
      this.setState({ examDetails: [], examDetailsLoading: false });
    }
  };

  // Per-level totals over every exam in scope: correct / wrong / skipped summed, accuracy =
  // total correct ÷ total attempted. Levels ordered by complexity id.
  levelTotals = () => {
    const byId = new Map();
    (this.state.examDetails || []).forEach((e) => (e.levels || []).forEach((l) => {
      const cur = byId.get(l.complexity_id) || { id: l.complexity_id, name: l.complexity, correct: 0, wrong: 0, skipped: 0, total: 0 };
      cur.correct += num(l.correct);
      cur.wrong += num(l.wrong);
      cur.skipped += num(l.skipped);
      cur.total += num(l.total);
      byId.set(l.complexity_id, cur);
    }));
    return [...byId.values()]
      .sort((a, b) => a.id - b.id)
      .map((t) => {
        const attempted = t.correct + t.wrong;
        return { ...t, attempted, accuracy: attempted ? Math.round((t.correct / attempted) * 100) : 0 };
      });
  };

  // Subject × level totals over every exam in scope (each exam's `subjects`): correct / wrong /
  // skipped summed per subject and level, accuracy = correct ÷ attempted. Also an overall
  // figure per subject across all its levels. Subjects A→Z, levels by complexity id.
  subjectTotals = () => {
    const subjects = new Map();
    const levelNames = new Map();
    (this.state.examDetails || []).forEach((e) => (e.subjects || []).forEach((s) => {
      const key = s.subject_id != null ? String(s.subject_id) : s.subject;
      const cur = subjects.get(key) || { key, name: s.subject || `Subject ${s.subject_id}`, levels: new Map(), exams: 0 };
      cur.exams += 1;
      (s.levels || []).forEach((l) => {
        levelNames.set(l.complexity_id, l.complexity);
        const lv = cur.levels.get(l.complexity_id) || { correct: 0, wrong: 0, skipped: 0, total: 0 };
        lv.correct += num(l.correct);
        lv.wrong += num(l.wrong);
        lv.skipped += num(l.skipped);
        lv.total += num(l.total);
        cur.levels.set(l.complexity_id, lv);
      });
      subjects.set(key, cur);
    }));
    const levels = [...levelNames.entries()].sort((a, b) => a[0] - b[0]).map(([id, name]) => ({ id, name }));
    const withAcc = (t) => {
      const attempted = t.correct + t.wrong;
      return { ...t, attempted, accuracy: attempted ? Math.round((t.correct / attempted) * 100) : null };
    };
    const rows = [...subjects.values()]
      .sort((a, b) => `${a.name}`.localeCompare(`${b.name}`))
      .map((s) => {
        const byLevel = {};
        const overall = { correct: 0, wrong: 0, skipped: 0, total: 0 };
        s.levels.forEach((lv, id) => {
          byLevel[id] = withAcc(lv);
          overall.correct += lv.correct; overall.wrong += lv.wrong; overall.skipped += lv.skipped; overall.total += lv.total;
        });
        return { key: s.key, name: s.name, exams: s.exams, byLevel, overall: withAcc(overall) };
      });
    return { levels, rows };
  };

  // Subject-wise Complexity — every subject's accuracy at each difficulty level, across the
  // exams in scope. Table / Graph toggle at the top right.
  renderSubjectWise = () => {
    const { levels, rows } = this.subjectTotals();
    if (!rows.length) return null;
    const view = this.state.subjectView;
    const seg = (v, icon, label) => (
      <button type="button" className={view === v ? "active" : ""} onClick={() => this.setState({ subjectView: v })}>
        <i className={`fas ${icon}`} />{label}
      </button>
    );
    const cell = (t) => (
      <>
        <span style={{ display: "inline-block", minWidth: 46, padding: "2px 8px", borderRadius: 8, fontWeight: 800, fontSize: 12.5, background: t.accuracy != null ? heatColor(t.accuracy) : "#f1f5f9", color: t.accuracy != null ? heatText(t.accuracy) : "#94a3b8" }}>
          {t.accuracy != null ? `${t.accuracy}%` : "—"}
        </span>
        <span className="d-block mt-1" style={{ fontSize: 10.5 }}>
          <span style={{ color: "#16a34a", fontWeight: 700 }}>{t.correct}✓</span>{" "}
          <span style={{ color: "#dc2626", fontWeight: 700 }}>{t.wrong}✗</span>{" "}
          <span className="text-muted">{t.skipped} skip</span>
        </span>
      </>
    );

    const chart = {
      chart: { type: "column", backgroundColor: "transparent", height: 320, style: { fontFamily: "'Outfit', sans-serif" } },
      title: { text: "" },
      credits: { enabled: false },
      legend: { enabled: true, align: "center", verticalAlign: "top", itemStyle: { fontSize: "12px", fontWeight: "600" } },
      xAxis: { categories: rows.map((r) => r.name), lineColor: "#e2e8f0", labels: { style: { color: "#64748b", fontWeight: "600" } } },
      yAxis: { min: 0, max: 100, title: { text: "Accuracy %", style: { color: "#94a3b8", fontSize: "11px" } }, gridLineColor: "#f1f5f9", labels: { style: { color: "#94a3b8" } } },
      tooltip: pointTooltip({ unit: "%" }),
      plotOptions: { column: { borderRadius: 4, borderWidth: 0, groupPadding: 0.12, dataLabels: { enabled: true, format: "{y}%", style: { fontSize: "10px", fontWeight: "700", color: "#475569", textOutline: "none" } } } },
      series: levels.map((l) => ({
        name: l.name,
        color: levelStyle(l.name).color,
        // A level the subject never attempted is a gap, not a 0% bar.
        data: rows.map((r) => (r.byLevel[l.id] && r.byLevel[l.id].accuracy != null ? r.byLevel[l.id].accuracy : null)),
      })),
    };

    return (
      <div className="p-3 mb-4" style={{ borderRadius: 14, border: "1px solid #f1f2f5", background: "#fff" }}>
        <div className="d-flex align-items-start justify-content-between flex-wrap mb-2" style={{ gap: 10 }}>
          <div>
            <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <i className="fas fa-table-cells mr-2" style={{ color: "#0ea5e9" }} />Subject-wise Complexity
            </h6>
            <span className="text-muted d-block" style={{ fontSize: 11.5 }}>
              Accuracy per subject at each difficulty level (correct ÷ attempted), across the exams below.
            </span>
          </div>
          <div className="sp-segment sp-segment-sm">
            {seg("table", "fa-table", "Table")}
            {seg("graph", "fa-chart-column", "Graph")}
          </div>
        </div>
        {view === "graph" ? (
          <ChartBoundary><HighchartsReact highcharts={Highcharts} options={chart} /></ChartBoundary>
        ) : (
          <div className="sp-table-wrap">
            <table className="sp-doubts-table" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th>Subject</th>
                  {levels.map((l) => (
                    <th key={l.id} className="text-center" style={{ color: levelStyle(l.name).color }}>{l.name}</th>
                  ))}
                  <th className="text-center" style={{ color: "#4f46e5" }}>Overall</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} style={{ cursor: "default" }}>
                    <td>
                      <div style={{ fontWeight: 700, color: "#334155" }}>{r.name}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>{r.overall.total} questions</div>
                    </td>
                    {levels.map((l) => {
                      const t = r.byLevel[l.id];
                      if (!t || t.total <= 0) return <td key={l.id} className="text-center text-muted">—</td>;
                      return (
                        <td key={l.id} className="text-center"
                          title={`${r.name} · ${l.name}: ${t.correct} correct, ${t.wrong} wrong, ${t.skipped} skipped of ${t.total}`}>
                          {cell(t)}
                        </td>
                      );
                    })}
                    <td className="text-center">{cell(r.overall)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // Question Type × Difficulty (getComplexityExamDetails → question_types): question types as
  // rows, Easy / Moderate / Difficult / Highly Difficult as columns. "All exams" adds up every
  // exam's counts; picking an exam (exam_paper_id / exam_name) shows just that one. Each cell:
  // accuracy = correct ÷ attempted (from the summed counts), with correct / attempted and the
  // question count underneath. Missing combinations show "—".
  renderQuestionTypeTable = () => {
    const exams = this.state.examDetails || [];
    const withQt = exams.filter((e) => (e.question_types || []).length > 0);
    if (!withQt.length) return null;

    const sel = this.state.qtExam;
    const qtView = this.state.qtView;
    const scoped = sel === "all" ? withQt : withQt.filter((e) => String(e.exam_paper_id) === String(sel));
    const inScope = scoped.length ? scoped : withQt;

    // Columns: the four fixed levels, then any other difficulty the data carries.
    const norm = (v) => `${v || ""}`.trim().toLowerCase();
    const extra = [];
    inScope.forEach((e) => (e.question_types || []).forEach((q) => {
      const d = `${q.difficulty || ""}`.trim();
      if (d && !QT_LEVELS.some((l) => norm(l) === norm(d)) && !extra.some((x) => norm(x) === norm(d))) extra.push(d);
    }));
    const columns = [...QT_LEVELS, ...extra];

    // Rows: question type → difficulty → summed counts.
    const rows = new Map();
    inScope.forEach((e) => (e.question_types || []).forEach((q) => {
      const rk = q.question_type_id != null ? `id:${q.question_type_id}` : `nm:${norm(q.question_type)}`;
      const row = rows.get(rk) || { name: q.question_type || "Other", cells: {}, total: 0, attempted: 0, correct: 0 };
      const ck = norm(q.difficulty);
      const c = row.cells[ck] || { total: 0, attempted: 0, correct: 0 };
      c.total += num(q.total_questions);
      c.attempted += num(q.attempted);
      c.correct += num(q.correct);
      row.cells[ck] = c;
      row.total += num(q.total_questions);
      row.attempted += num(q.attempted);
      row.correct += num(q.correct);
      rows.set(rk, row);
    }));
    const list = [...rows.values()].sort((a, b) => `${a.name}`.localeCompare(`${b.name}`));
    const acc = (c) => (c.attempted > 0 ? Math.round((c.correct / c.attempted) * 100) : null);

    const cell = (c) => {
      if (!c || c.total <= 0) return <span className="text-muted">—</span>;
      const a = acc(c);
      return (
        <>
          <span style={{ display: "inline-block", minWidth: 46, padding: "2px 8px", borderRadius: 8, fontWeight: 800, fontSize: 12.5, background: a != null ? heatColor(a) : "#f1f5f9", color: a != null ? heatText(a) : "#94a3b8" }}>
            {a != null ? `${a}%` : "0 att."}
          </span>
          {/* <span className="d-block mt-1 text-muted" style={{ fontSize: 10.5 }}>
            {c.correct}/{c.attempted} correct · {c.total} qs
          </span> */}
        </>
      );
    };

    return (
      <div className="p-3 mb-4" style={{ borderRadius: 14, border: "1px solid #f1f2f5", background: "#fff" }}>
        <div className="d-flex align-items-start justify-content-between flex-wrap mb-3" style={{ gap: 10 }}>
          <div>
            <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <i className="fas fa-list-check mr-2" style={{ color: "#8b5cf6" }} />Question Type × Difficulty
            </h6>
            <span className="text-muted d-block" style={{ fontSize: 11.5 }}>
              Accuracy (correct ÷ attempted) for each question type at each difficulty, with the question count.
            </span>
          </div>
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 10 }}>
            <select
              value={sel}
              onChange={(ev) => this.setState({ qtExam: ev.target.value })}
              style={{ fontSize: 12.5, padding: "6px 10px", borderRadius: 8, border: "1px solid #e2e8f0", color: "#334155", background: "#fff", maxWidth: 280 }}
            >
              <option value="all">All exams ({withQt.length})</option>
              {withQt.map((e) => (
                <option key={e.exam_paper_id} value={String(e.exam_paper_id)}>{e.exam_name || `Exam ${e.exam_paper_id}`}</option>
              ))}
            </select>
            {/* Table / Graph toggle */}
            <div className="sp-segment sp-segment-sm">
              <button type="button" className={qtView === "table" ? "active" : ""} onClick={() => this.setState({ qtView: "table" })}>
                <i className="fas fa-table" />Table
              </button>
              <button type="button" className={qtView === "graph" ? "active" : ""} onClick={() => this.setState({ qtView: "graph" })}>
                <i className="fas fa-chart-column" />Graph
              </button>
            </div>
          </div>
        </div>
        {!list.length ? (
          <div className="text-center text-muted py-4" style={{ fontSize: 13 }}>No question-type data for this exam.</div>
        ) : qtView === "graph" ? (
          // Grouped bars: question types along the bottom, one bar per difficulty (accuracy %).
          // A combination with no questions, or none attempted, is a gap — not a 0% bar.
          <ChartBoundary>
            <HighchartsReact
              highcharts={Highcharts}
              options={{
                chart: { type: "column", backgroundColor: "transparent", height: 340, style: { fontFamily: "'Outfit', sans-serif" } },
                title: { text: "" },
                credits: { enabled: false },
                legend: { enabled: true, align: "center", verticalAlign: "top", itemStyle: { fontSize: "12px", fontWeight: "600" } },
                xAxis: { categories: list.map((r) => r.name), lineColor: "#e2e8f0", labels: { style: { color: "#64748b", fontWeight: "600" } } },
                yAxis: { min: 0, max: 100, title: { text: "Accuracy %", style: { color: "#94a3b8", fontSize: "11px" } }, gridLineColor: "#f1f5f9", labels: { style: { color: "#94a3b8" } } },
                tooltip: {
                  useHTML: true,
                  formatter: function () {
                    const p = this.point || {};
                    return `<div style="padding:2px 4px;min-width:150px">`
                      + `<div style="font-size:11px;color:#94a3b8">${this.x}</div>`
                      + `<div style="font-weight:700;color:${this.series.color}">${this.series.name}: ${this.y}%</div>`
                      + `<div style="font-size:11.5px;color:#475569">${p.correct}/${p.attempted} correct · ${p.total} qs</div>`
                      + `</div>`;
                  },
                },
                plotOptions: { column: { borderRadius: 4, borderWidth: 0, groupPadding: 0.12, dataLabels: { enabled: true, format: "{y}%", style: { fontSize: "10px", fontWeight: "700", color: "#475569", textOutline: "none" } } } },
                series: columns.map((l) => ({
                  name: l,
                  color: levelStyle(l).color,
                  data: list.map((r) => {
                    const c = r.cells[norm(l)];
                    if (!c || c.total <= 0 || c.attempted <= 0) return null;
                    return { y: acc(c), correct: c.correct, attempted: c.attempted, total: c.total };
                  }),
                })).filter((s) => s.data.some((d) => d != null)),
              }}
            />
          </ChartBoundary>
        ) : (
          <div className="sp-table-wrap">
            <table className="sp-doubts-table" style={{ minWidth: 680 }}>
              <thead>
                <tr>
                  <th>Question Type</th>
                  {columns.map((l) => (
                    <th key={l} className="text-center" style={{ color: levelStyle(l).color }}>{l}</th>
                  ))}
                  {/* <th className="text-center" style={{ color: "#4f46e5" }}>Overall</th> */}
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.name} style={{ cursor: "default" }}>
                    <td style={{ fontWeight: 700, color: "#334155" }}>{r.name}</td>
                    {columns.map((l) => (
                      <td key={l} className="text-center">{cell(r.cells[norm(l)])}</td>
                    ))}
                    {/* <td className="text-center">{cell(r)}</td> */}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // KPI tiles — accuracy per difficulty level over the exams in scope.
  renderKpis =  (levels) => (
    <div className="sp-stat-grid mb-4">
      {levels.map((l) => {
        const st = levelStyle(l.name);
        return (
          <div
            key={l.id}
            className="sp-summary-card d-flex align-items-start justify-content-between p-3 h-100"
            style={{ borderRadius: "16px", background: "#ffffff", border: "1px solid #f1f2f5", boxShadow: "0 6px 18px rgba(0,0,0,0.05)" }}
          >
            <div>
              <span className="d-block text-muted" style={{ fontSize: "13px", fontWeight: 600 }}>{l.name}</span>
              <strong className="d-block text-dark" style={{ fontSize: "2rem", fontFamily: "'Outfit', sans-serif", lineHeight: 1.2 }}>
                {l.attempted ? `${l.accuracy}%` : "—"}
              </strong>
              <span className="d-block" style={{ fontSize: "12px", fontWeight: 600, color: st.color }}>
                <i className="fas fa-graduation-cap mr-1" style={{ fontSize: 10 }} />{l.correct} of {l.attempted} correct
              </span>
            </div>
            <div
              className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
              style={{ width: "44px", height: "44px", borderRadius: "12px", background: st.iconBg, boxShadow: "0 6px 14px rgba(0,0,0,0.12)" }}
            >
              <i className={`fas ${st.icon}`} style={{ fontSize: "18px" }} />
            </div>
          </div>
        );
      })}
    </div>
  );

  // Accuracy by difficulty over the exams in scope.
  renderBarChart = (levels) => {
    if (!levels.length) return null;
    const options = {
      chart: { type: "column", backgroundColor: "transparent", height: 300, style: { fontFamily: "'Outfit', sans-serif" } },
      title: { text: "" },
      credits: { enabled: false },
      legend: { enabled: false },
      xAxis: { categories: levels.map((l) => l.name), lineColor: "#e2e8f0", labels: { style: { color: "#64748b", fontWeight: "600" } } },
      yAxis: { min: 0, max: 100, title: { text: "Accuracy %", style: { color: "#94a3b8", fontSize: "11px" } }, gridLineColor: "#f1f5f9", labels: { style: { color: "#94a3b8" } } },
      tooltip: pointTooltip({ unit: "%" }),
      plotOptions: { column: { borderRadius: 4, borderWidth: 0, dataLabels: { enabled: true, format: "{y}%", style: { fontSize: "10px", fontWeight: "700", color: "#475569", textOutline: "none" } } } },
      series: [{ name: "Exam accuracy", data: levels.map((l) => ({ y: l.accuracy, color: levelStyle(l.name).color })) }],
    };
    return (
      <div className="p-3 mb-4" style={{ borderRadius: 14, border: "1px solid #f1f2f5", background: "#fff" }}>
        <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
          <i className="fas fa-chart-column mr-2" style={{ color: "#4f46e5" }} />Accuracy by Difficulty
        </h6>
        <span className="text-muted d-block mb-2" style={{ fontSize: 11.5 }}>Correct ÷ attempted at each difficulty level, across the exams below.</span>
        <ChartBoundary><HighchartsReact highcharts={Highcharts} options={options} /></ChartBoundary>
      </div>
    );
  };

  // Exam-wise Complexity (getComplexityExamDetails): one row per exam, newest first; one
  // column per complexity level the exams carry, ordered by complexity id. Each cell shows
  // the level's accuracy (correct ÷ attempted) over correct / wrong / skipped counts.
  renderExamDetails = () => {
    const { examDetails, examDetailsLoading, examView } = this.state;
    const hasRows = !examDetailsLoading && examDetails && examDetails.length > 0;
    const seg = (v, icon, label) => (
      <button type="button" className={examView === v ? "active" : ""} onClick={() => this.setState({ examView: v })}>
        <i className={`fas ${icon}`} />{label}
      </button>
    );
    const card = (body) => (
      <div className="p-3 mt-4" style={{ borderRadius: 14, border: "1px solid #f1f2f5", background: "#fff" }}>
        <div className="d-flex align-items-start justify-content-between flex-wrap mb-3" style={{ gap: 10 }}>
          <div>
            <h6 className="font-weight-bold text-dark mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <i className="fas fa-file-lines mr-2" style={{ color: "#4f46e5" }} />Exam-wise Complexity
            </h6>
            <span className="text-muted d-block" style={{ fontSize: 11.5 }}>
              Every exam of the selected exam in the last year — accuracy at each difficulty level, with correct / wrong / skipped questions.
            </span>
          </div>
          {hasRows ? (
            <div className="sp-segment sp-segment-sm">
              {seg("table", "fa-table", "Table")}
              {seg("graph", "fa-chart-line", "Graph")}
            </div>
          ) : null}
        </div>
        {body}
      </div>
    );
    if (examDetailsLoading) {
      return card(<div className="text-center text-muted py-4" style={{ fontSize: 13 }}><i className="fas fa-circle-notch fa-spin mr-2" />Loading exams…</div>);
    }
    if (!examDetails || !examDetails.length) {
      return card(<div className="text-center text-muted py-4" style={{ fontSize: 13 }}>No exam-wise complexity data for this selection.</div>);
    }

    // Columns: the union of levels across exams, by complexity id.
    const levelMap = new Map();
    examDetails.forEach((e) => (e.levels || []).forEach((l) => {
      if (!levelMap.has(l.complexity_id)) levelMap.set(l.complexity_id, l.complexity);
    }));
    const levels = [...levelMap.entries()].sort((a, b) => a[0] - b[0]);
    const fmtDate = (ts) => {
      const n = Number(ts);
      if (!n) return "";
      const m = n < 1000000000000 ? moment.unix(n) : moment(n);
      return m.isValid() ? m.format("DD MMM YYYY") : "";
    };
    const exams = [...examDetails].sort((a, b) => Number(b.timestamp || 0) - Number(a.timestamp || 0));

    // Graph: one line per difficulty level across the exams, oldest → newest, so each level's
    // accuracy reads as a trend. Every exam is shown:
    //  - a level the exam had questions at but none attempted plots at 0% with a hollow marker
    //    ("0 attempted" in the tooltip) — it used to be dropped, so an all-skipped exam vanished;
    //  - a level the exam had no questions at is skipped, and the line joins its neighbours;
    //  - an exam with no difficulty-tagged questions at all gets a labelled band, not a blank.
    if (examView === "graph") {
      const byDate = [...exams].reverse();
      const levelOf = (e, id) => (e.levels || []).find((l) => l.complexity_id === id);
      const noLevelBands = byDate
        .map((e, i) => (!(e.levels || []).some((l) => num(l.total) > 0) ? i : null))
        .filter((i) => i != null)
        .map((i) => ({
          from: i - 0.4, to: i + 0.4, color: "#f8fafc",
          label: { text: "No difficulty data", rotation: -90, verticalAlign: "middle", style: { color: "#94a3b8", fontSize: "10.5px", fontWeight: "600" } },
        }));
      const options = {
        chart: { type: "spline", backgroundColor: "transparent", height: 340, style: { fontFamily: "'Outfit', sans-serif" } },
        title: { text: "" },
        credits: { enabled: false },
        legend: { enabled: true, align: "center", verticalAlign: "top", itemStyle: { fontSize: "12px", fontWeight: "600" } },
        xAxis: {
          categories: byDate.map((e) => e.exam_name || `Exam ${e.exam_paper_id}`),
          lineColor: "#e2e8f0",
          labels: { rotation: byDate.length > 4 ? -35 : 0, style: { color: "#64748b", fontWeight: "600", fontSize: "11px" } },
          plotBands: noLevelBands,
        },
        yAxis: { min: 0, max: 100, title: { text: "Accuracy %", style: { color: "#94a3b8", fontSize: "11px" } }, gridLineColor: "#f1f5f9", labels: { style: { color: "#94a3b8" } } },
        tooltip: {
          useHTML: true,
          formatter: function () {
            const p = this.point || {};
            return `<div style="padding:2px 4px;min-width:150px">`
              + `<div style="font-size:11px;color:#94a3b8">${this.x}${p.date ? ` · ${p.date}` : ""}</div>`
              + `<div style="font-weight:700;color:${this.series.color}">${this.series.name}: ${p.noAttempt ? "0 attempted" : `${this.y}%`}</div>`
              + `<div style="font-size:11.5px;color:#475569">${p.correct} correct · ${p.wrong} wrong · ${p.skipped} skipped</div>`
              + `</div>`;
          },
        },
        plotOptions: {
          spline: {
            lineWidth: 3,
            marker: { enabled: true, radius: 4 },
            dataLabels: { enabled: true, format: "{y}%", style: { fontSize: "10px", fontWeight: "700", color: "#475569", textOutline: "2px #fff" } },
          },
        },
        series: levels.map(([id, name]) => ({
          name,
          color: levelStyle(name).color,
          // Join across exams that had no questions at this level, so the trend stays one line.
          connectNulls: true,
          data: byDate.map((e) => {
            const l = levelOf(e, id);
            if (!l || num(l.total) <= 0) return null;
            const base = { correct: num(l.correct), wrong: num(l.wrong), skipped: num(l.skipped), date: fmtDate(e.timestamp) };
            if (num(l.attempted) <= 0) {
              // Questions at this level, none attempted: 0%, hollow marker, labelled.
              return {
                ...base, y: 0, noAttempt: true,
                marker: { fillColor: "#ffffff", lineWidth: 2, lineColor: levelStyle(name).color },
                dataLabels: { format: "0 att." },
              };
            }
            return { ...base, y: Math.round(num(l.accuracy)) };
          }),
        })),
      };
      return card(<ChartBoundary><HighchartsReact highcharts={Highcharts} options={options} /></ChartBoundary>);
    }

    return card(
      <div className="sp-table-wrap">
        <table className="sp-doubts-table" style={{ minWidth: 640 }}>
          <thead>
            <tr>
              <th>Exam</th>
              <th className="text-center" style={{ width: 90 }}>Questions</th>
              {levels.map(([id, name]) => (
                <th key={id} className="text-center" style={{ color: levelStyle(name).color }}>{name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {exams.map((e) => {
              const byId = {};
              (e.levels || []).forEach((l) => { byId[l.complexity_id] = l; });
              return (
                <tr key={e.exam_paper_id} style={{ cursor: "default" }}>
                  <td>
                    <div style={{ fontWeight: 700, color: "#334155" }}>{e.exam_name || `Exam ${e.exam_paper_id}`}</div>
                    {fmtDate(e.timestamp) ? <div className="text-muted" style={{ fontSize: 11 }}>{fmtDate(e.timestamp)}</div> : null}
                  </td>
                  <td className="text-center text-muted">{num(e.total_questions)}</td>
                  {levels.map(([id]) => {
                    const l = byId[id];
                    if (!l || num(l.total) <= 0) return <td key={id} className="text-center text-muted">—</td>;
                    const attempted = num(l.attempted);
                    return (
                      <td key={id} className="text-center"
                        title={`${l.complexity}: ${num(l.correct)} correct, ${num(l.wrong)} wrong, ${num(l.skipped)} skipped of ${num(l.total)}`}>
                        <span style={{ display: "inline-block", minWidth: 46, padding: "2px 8px", borderRadius: 8, fontWeight: 800, fontSize: 12.5, background: attempted ? heatColor(l.accuracy) : "#f1f5f9", color: attempted ? heatText(l.accuracy) : "#94a3b8" }}>
                          {attempted ? `${Math.round(num(l.accuracy))}%` : "—"}
                        </span>
                        <span className="d-block mt-1" style={{ fontSize: 10.5 }}>
                          <span style={{ color: "#16a34a", fontWeight: 700 }}>{num(l.correct)}✓</span>{" "}
                          <span style={{ color: "#dc2626", fontWeight: 700 }}>{num(l.wrong)}✗</span>{" "}
                          <span className="text-muted">{num(l.skipped)} skip</span>
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  renderLocked = () => (
    <div className="text-center py-5">
      <i className="fas fa-lock d-block mb-2" style={{ fontSize: 30, color: "#cbd5e1" }} />
      {Cookies.get("student_userlevel") === "1" ? (
        <div className="text-danger" style={{ fontSize: 14 }}>You now have limited access.</div>
      ) : (
        <>
          <div className="text-danger mb-1" style={{ fontSize: 14 }}>You now have limited access. For full access:</div>
          <Link to="/student/package" style={{ color: "#4f46e5", fontWeight: 600 }}>Subscribe Now</Link>
        </>
      )}
    </div>
  );

  render() {
    const { examDetailsLoading, locked } = this.state;
    const levels = this.levelTotals();

    return (
      <div className="sp-content-card mt-3" style={{ marginTop: 0 }}>
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-1" style={{ gap: 10 }}>
          <h6 className="font-weight-bold text-dark mb-0" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <i className="far fa-layer-group mr-2" style={{ color: "#4f46e5" }} />Complexity Analysis
          </h6>
        </div>
        <span className="text-muted d-block mb-3" style={{ fontSize: 12.5 }}>Performance across difficulty levels — where the student is strong and where accuracy drops.</span>

        {examDetailsLoading ? (
          <PageShimmer />
        ) : locked ? (
          this.renderLocked()
        ) : !levels.length ? (
          <div className="text-center text-muted py-5" style={{ fontSize: 14 }}>
            <i className="far fa-layer-group d-block mb-2" style={{ fontSize: 30, color: "#cbd5e1" }} />
            No complexity data for this selection.
          </div>
        ) : (
          <>
            {this.renderKpis(levels)}
            {this.renderBarChart(levels)}
            {this.renderSubjectWise()}
            {this.renderQuestionTypeTable()}
            {this.renderExamDetails()}
          </>
        )}
      </div>
    );
  }
}

export default compose(withApollo, withRouter)(ComplexityTab);
